import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useDebounce } from "hooks/useDebounce";
import React, { useEffect, useRef, useState } from "react";

export interface AutosaveFormTextFieldProps {
  onSave: (value: string) => Promise<void>;
  delay?: number;
  showSaveIndicator?: boolean;
  clearSavedIndicatorAfterDelay?: boolean;
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  disabled?: boolean;
}

export function AutosaveFormTextField({
  onSave,
  delay = 1000,
  showSaveIndicator = true,
  clearSavedIndicatorAfterDelay = false,
  defaultValue = "",
  label,
  placeholder,
  multiline = false,
  rows = 1,
  fullWidth = true,
  disabled = false,
}: AutosaveFormTextFieldProps) {
  const theme = useTheme();
  const [value, setValue] = useState<string>(defaultValue);
  const [lastSavedValue, setLastSavedValue] = useState<string>(defaultValue);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [showPendingIndicator, setShowPendingIndicator] = useState(false);
  const pendingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedValue] = useDebounce(value, delay);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    setValue(newValue);
    setShowSavedIndicator(false);

    // Clear existing timer
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    // Show pending indicator after 250ms only if the value has actually changed
    if (newValue !== lastSavedValue && newValue !== defaultValue) {
      const timer = setTimeout(() => {
        setShowPendingIndicator(true);
      }, 250);
      pendingTimerRef.current = timer;
    } else if (newValue === lastSavedValue) {
      // Clear pending indicator but dont save (on the backend) if the user types
      // but the value is the same as the last saved value
      setShowPendingIndicator(false);
      setShowSavedIndicator(true);

      if (clearSavedIndicatorAfterDelay) {
        setTimeout(() => setShowSavedIndicator(false), delay);
      }
    }
  };

  // Save after debounce delay
  useEffect(() => {
    const save = async () => {
      if (
        debouncedValue !== lastSavedValue &&
        debouncedValue !== defaultValue
      ) {
        // Clear pending timer immediately when starting save
        if (pendingTimerRef.current) {
          clearTimeout(pendingTimerRef.current);
          pendingTimerRef.current = null;
        }

        try {
          await onSave(debouncedValue);
          setLastSavedValue(debouncedValue);
          setShowPendingIndicator(false);
          setShowSavedIndicator(true);

          if (clearSavedIndicatorAfterDelay) {
            setTimeout(() => setShowSavedIndicator(false), delay);
          }
        } catch (error) {
          console.error("Failed to save value:", error);
          setShowPendingIndicator(false);
        }
      }
    };
    save();
  }, [
    debouncedValue,
    lastSavedValue,
    defaultValue,
    onSave,
    delay,
    clearSavedIndicatorAfterDelay,
  ]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey50.main,
        px: 2,
        py: 0.5,
        borderRadius: 1,
        minHeight: 50,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Label */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontSize: "0.75rem",
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>

      {/* Input Field */}
      <TextField
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        multiline={multiline}
        minRows={rows}
        maxRows={multiline ? 4 : 1}
        fullWidth={fullWidth}
        disabled={disabled}
        variant="standard"
        slotProps={{
          input: {
            disableUnderline: true,
          },
        }}
        sx={{
          margin: 0,
          "& .MuiInput-root": {
            fontSize: "1rem",
            color: "text.primary",
            alignItems: "flex-start",
            mt: 0,
            pt: 0,
            pb: 0,
          },
          "& .MuiInput-root:before": {
            borderBottom: "none",
          },
          "& .MuiInput-root:after": {
            borderBottom: "none",
          },
          "& .MuiInput-root:hover:before": {
            borderBottom: "none",
          },
          "& .MuiInputBase-input": {
            padding: 0,
            wordBreak: "break-word",
            whiteSpace: multiline ? "pre-wrap" : "nowrap",
            overflow: multiline ? "visible" : "hidden",
            textOverflow: multiline ? "clip" : "ellipsis",
            resize: "none",
            lineHeight: 1.43,
            minHeight: "1rem",
          },
        }}
      />

      {/* Save Indicators */}
      {showSaveIndicator && (
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {showPendingIndicator && <CircularProgress size={16} />}
          {showSavedIndicator && (
            <CheckCircle
              sx={{
                color: "success.main",
                fontSize: 16,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
